// Marketing pages for each audience, each served at /<slug> (e.g. /freshers).
// The slug must not clash with another top-level route.
//
// Edit the copy here; the layout lives in src/pages/Audience and the shared
// marketing blocks in src/components/marketing.
//
// Sections are optional: a page shows `features`, `offer` ('team' or 'expert'),
// `live`, `prep` and `compare` only if it defines them. CTA links starting
// with # scroll within the page.
//
// Keep claims true: no made-up numbers, salaries, placements or testimonials.
// Live class courses are matched by title against src/data/liveClasses.js, so
// they always show the next real cohort date.

// Feature rows used by pages that sell the Real Engineering Team
const teamFeatures = (letter) => {
  const sentence = letter.charAt(0) + letter.slice(1).toLowerCase();
  return [
  {
    visual: 'ai',
    kicker: 'Work with AI engineers',
    title: 'Build software the way the best teams build it now: with AI',
    text: 'You join a small team led by a senior engineer and work alongside AI engineers on real products. AI coding tools are part of your everyday workflow, and you learn the part most people skip: reviewing what the AI writes.',
    bullets: [
      'AI coding assistants like Claude Code, Cursor and GitHub Copilot',
      'LLM APIs, tool use and agents inside real features',
      'Real tickets, sprints, code reviews and deployments'
    ]
  },
  {
    visual: 'documents',
    kicker: 'Proof that counts',
    title: 'Walk out with documents, not just knowledge',
    text: `When you complete the programme you get an ${letter.toLowerCase()} for the work you shipped, and pay slips for your time on the team. Real paperwork to back up the real experience on your CV.`,
    bullets: [`${sentence} on completion`, 'Pay slips for the programme', 'Real work you can show and explain']
  },
  {
    visual: 'interview',
    kicker: 'Interview guidance',
    title: 'Turn the work you did into the offer you want',
    text: 'Experience only helps if you can talk about it. We help you shape your CV, practise mock interviews, and tell the story of what you built so interviewers remember you.',
    bullets: ['Resume reviews', 'Mock interviews with feedback', 'Project stories that hold up in deep-dives']
  },
  {
    visual: 'support',
    kicker: 'We stay with you',
    title: 'Your first job, with someone in your corner',
    text: 'Getting hired is not the finish line. After you land the job, we are still here to help you settle in, handle your first big tasks and keep growing.',
    bullets: ['Help in your first weeks on the job', 'Someone to ask when you are stuck', 'Guidance as you grow']
  }
  ];
};

const teamCompareRows = (letter) => [
  ['Who you learn with', 'Pre-recorded videos', 'Senior engineers and AI engineers'],
  ['What you build', 'Tutorial projects', 'Real software, shipped to production'],
  ['How you learn the job', 'Theory and quizzes', 'Tickets, PRs, code reviews, standups'],
  ['AI tools', 'Rarely covered', 'Used every single day'],
  ['What you leave with', 'A certificate', `${letter} and pay slips`],
  ['After you finish', 'You are on your own', 'Interview guidance and support after you are hired']
];

export const audiences = [
  {
    slug: 'final-year-students',
    navLabel: 'Final-year students',
    accent: ['#22d3ee', '#a855f7'],
    seo: {
      title: 'For final-year engineering students | Codeazy',
      description: 'Graduate with real engineering experience: join a real team, build with AI tools, and get an internship letter before campus placements.'
    },
    hero: {
      kicker: 'For final-year engineering students',
      title: 'Your degree gets you shortlisted.',
      highlight: 'Experience gets you hired.',
      text: 'Spend your final year on a real engineering team, shipping real software with AI engineers. Walk into placements with an internship letter, real work to show, and stories no other fresher has.',
      ctas: [
        { label: 'Join the real engineering team', to: '#offer', primary: true },
        { label: 'Explore live classes', to: '#live-classes' }
      ],
      proof: ['Internship letter', 'Pay slips', 'Interview guidance', 'Support after you are hired'],
      visual: { type: 'team', letter: 'Internship Letter' }
    },
    ticker: ['Real tickets', 'Real code reviews', 'Real deployments', 'AI-first tools', 'Internship letter', 'Pay slips', 'Interview guidance', 'Support after you are hired'],
    statement: {
      lead: 'Every placement season looks the same.',
      struck: ['Same to-do app projects.', 'Same tutorial certificates.', 'Same nervous interview answers.'],
      answer: 'Be the fresher who has already worked like an engineer.'
    },
    beforeAfter: {
      before: {
        title: 'Graduating the usual way',
        items: ['Tutorial projects nobody uses', 'No idea how real teams work', 'A CV that looks like everyone else’s', 'Interview answers based on theory']
      },
      after: {
        title: 'Graduating with Codeazy',
        items: ['Real features shipped to production', 'Standups, pull requests and code reviews', 'An internship letter and pay slips', 'Project stories that win interviews']
      }
    },
    featuresHead: {
      kicker: 'Join the real engineering team',
      title: 'Your final-year internship, on a real engineering team',
      text: 'Not another course. A team, a backlog and a senior engineer reviewing your code.'
    },
    features: teamFeatures('Internship Letter'),
    offer: {
      type: 'team',
      letter: 'Internship letter',
      title: 'Make your final year the one that changes everything',
      text: 'Three months on a real engineering team, planned around your semester, so you finish before or during placement season.',
      sticky: 'Final-year batch · limited seats'
    },
    live: {
      title: 'Nail the fundamentals placements test',
      text: 'Pair the team experience with live, mentor-led classes on exactly what placement rounds ask.',
      courses: ['Data Structures & Algorithms', 'Full-Stack Web Development', 'Java Full-Stack']
    },
    compare: {
      title: 'Not another online course',
      them: 'A typical online course',
      us: 'Codeazy engineering team',
      rows: teamCompareRows('Internship letter')
    },
    journey: {
      title: 'Your final year, planned',
      steps: [
        { when: 'Semester 7', title: 'Fundamentals', text: 'DSA patterns and one stack (MERN or Java) in live classes.' },
        { when: 'Semester 7–8', title: 'Real team', text: 'Ship features with AI engineers and get every PR reviewed.' },
        { when: 'Placement season', title: 'Interview-ready', text: 'Mock interviews, resume review and polished project stories.' },
        { when: 'After the offer', title: 'Day-one ready', text: 'Start your job used to real codebases, with our support.' }
      ]
    },
    faqs: [
      { q: 'Can I do this alongside college?', a: 'Yes. Live classes run on weekday evenings and weekends, and the team programme is planned around your schedule. Tell us your semester dates and we will help you plan it.' },
      { q: 'Will my college accept the internship letter?', a: 'You receive an internship letter when you complete the programme. Colleges have different rules for internship credit, so check with your department; we can share programme details they may ask for.' },
      { q: 'Do I need prior experience?', a: 'No. You need basic programming knowledge and the willingness to learn. If you are not ready yet, start with live classes and join the team after.' },
      { q: 'Team or live classes: which first?', a: 'If your fundamentals are shaky, start with live classes. If you can already build small projects, join the team. Not sure? Talk to a mentor and get a recommendation.' }
    ],
    finalCta: {
      title: 'Don’t just graduate. Graduate ready.',
      text: 'Seats on every team are limited. Lock in your final-year internship before placement season starts.',
      ctas: [
        { label: 'Join the real engineering team', to: '#offer', primary: true },
        { label: 'Talk to a mentor', to: '/#book-session' }
      ]
    }
  },
  {
    slug: 'freshers',
    navLabel: 'Graduates looking for a job',
    accent: ['#fbbf24', '#f43f5e'],
    seo: {
      title: 'For graduates still looking for a job | Codeazy',
      description: 'Graduated but no job yet? Get real engineering experience on a team with AI engineers, an experience letter, pay slips and interview guidance.'
    },
    hero: {
      kicker: 'For graduates still looking for their first job',
      title: 'No experience, no job. No job, no experience.',
      highlight: 'Let’s break the loop.',
      text: 'Join a real engineering team, ship real software with AI engineers, and come out with an experience letter, pay slips and the confidence to finally convert your interviews.',
      ctas: [
        { label: 'Get real experience', to: '#offer', primary: true },
        { label: 'Explore live classes', to: '#live-classes' }
      ],
      proof: ['Experience letter', 'Pay slips', 'Interview guidance', 'Support after you are hired'],
      visual: { type: 'team', letter: 'Experience Letter' }
    },
    ticker: ['Real experience', 'Experience letter', 'Pay slips', 'AI engineers', 'Code reviews', 'Production deploys', 'Interview guidance', 'Support after you are hired'],
    statement: {
      lead: 'You have done everything they told you to.',
      struck: ['Another online course.', 'Another certificate.', 'Another application with no reply.'],
      answer: 'You don’t need more courses. You need real experience.'
    },
    beforeAfter: {
      before: {
        title: 'Job hunting today',
        items: ['“Experience required” on every listing', 'A growing gap on your CV', 'Applications that never get a reply', 'Interviews that end at the project round']
      },
      after: {
        title: 'After the engineering team',
        items: ['Real experience on real software', 'An experience letter and pay slips', 'A CV worth shortlisting', 'Project stories you can defend in any round']
      }
    },
    featuresHead: {
      kicker: 'Join the real engineering team',
      title: 'Real experience, not another certificate',
      text: 'Work the way you would in your first job, from day one.'
    },
    features: teamFeatures('Experience Letter'),
    offer: {
      type: 'team',
      letter: 'Experience letter',
      title: 'Turn the gap on your CV into real experience',
      text: 'Three months on a real engineering team, with the documents and interview guidance to turn it into offers.',
      sticky: 'Next team batch · limited seats'
    },
    live: {
      title: 'Close the gaps interviews keep exposing',
      text: 'Live, mentor-led classes with real-time doubt solving, so the next interview goes differently.',
      courses: ['Full-Stack Web Development', 'Java Full-Stack', 'Data Structures & Algorithms']
    },
    compare: {
      title: 'Why another course won’t fix it',
      them: 'Another online course',
      us: 'Codeazy engineering team',
      rows: teamCompareRows('Experience letter')
    },
    journey: {
      title: 'From job hunting to hired',
      steps: [
        { when: 'Week 1', title: 'Find your gaps', text: 'A mentor reviews your skills, CV and target roles.' },
        { when: 'Months 1–3', title: 'Work on a real team', text: 'Ship features with AI engineers; fill gaps in live classes.' },
        { when: 'Alongside', title: 'Interview guidance', text: 'CV rewrite, mock interviews and targeted applications.' },
        { when: 'After you are hired', title: 'Ongoing support', text: 'Help settling into your new job and growing in it.' }
      ]
    },
    faqs: [
      { q: 'Does this count as work experience?', a: 'You work on real software with an engineering team, and receive an experience letter and pay slips for the programme. How a specific employer weighs it varies, but you will have real work, documents and stories to back it up.' },
      { q: 'I graduated a year or two ago. Is it too late?', a: 'No. Many people join after a gap. What matters is showing recent, real work, and the programme gives you exactly that.' },
      { q: 'Do you guarantee a job?', a: 'No one can honestly guarantee a job. We give you real experience, interview guidance and support through your search and after you are hired.' },
      { q: 'I am from a non-CS branch. Can I join?', a: 'Yes, if you can program at a basic level. Starting from zero? Begin with a live class and move to the team once your fundamentals are in place.' }
    ],
    finalCta: {
      title: 'Your first engineering job starts here.',
      text: 'Stop waiting for someone to give you experience. Go and get it.',
      ctas: [
        { label: 'Get real experience', to: '#offer', primary: true },
        { label: 'Talk to a mentor', to: '/#book-session' }
      ]
    }
  },
  {
    slug: 'working-professionals',
    navLabel: 'Working professionals',
    accent: ['#34d399', '#3b82f6'],
    seo: {
      title: 'For software engineers who want to upskill | Codeazy',
      description: 'Talk to a senior engineer, upskill in live classes and prepare with in-depth interview questions to move to a better role at a product company.'
    },
    hero: {
      kicker: 'For working software engineers',
      title: 'You’ve outgrown your job.',
      highlight: 'Go get the offer you deserve.',
      text: 'Product companies and AI-first teams pay for skills you can prove. Get a plan from a senior engineer, sharpen DSA, system design and AI in live classes, and walk into interviews prepared.',
      ctas: [
        { label: 'Talk to an expert', to: '#offer', primary: true },
        { label: 'Explore live classes', to: '#live-classes' }
      ],
      proof: ['1:1 with a senior engineer', 'Evening and weekend batches', 'Free in-depth interview prep'],
      visual: { type: 'expert' }
    },
    ticker: ['Product company interviews', 'System design', 'DSA patterns', 'Agentic AI', '1:1 expert call', 'Live mentor-led classes', 'Free interview prep'],
    statement: {
      lead: 'Talent isn’t what’s holding you back.',
      struck: ['Another year of maintenance tickets.', 'Another small annual hike.', 'Another course you never finish.'],
      answer: 'What you need is a plan, and the right preparation.'
    },
    beforeAfter: {
      before: {
        title: 'Preparing alone',
        items: ['Random problems with no pattern', 'System design from scattered videos', 'No idea what your target companies expect', 'A CV that undersells your experience']
      },
      after: {
        title: 'Preparing with Codeazy',
        items: ['A plan built by a senior engineer', 'DSA, system design and AI, taught live', 'Interview questions answered in depth', 'A CV reviewed for product companies']
      }
    },
    featuresHead: {
      kicker: 'How we get you there',
      title: 'Plan it. Learn it. Practise it.',
      text: 'Three ways we help you move to a better role, built around a full-time job.'
    },
    features: [
      {
        visual: 'expert',
        kicker: 'Talk to an expert',
        title: '30 minutes with a senior engineer can save you months',
        text: 'Before you spend months preparing, get an honest look at where you stand and a plan built for you: what to learn, what to skip, and in what order.',
        bullets: ['An honest check of your skills against target roles', 'Line-by-line CV review', 'A step-by-step path to the switch']
      },
      {
        visual: 'live',
        kicker: 'Live classes',
        title: 'Learn what senior rounds actually test, live',
        text: 'Mentor-led classes in evening and weekend batches, with real-time doubt solving. No pre-recorded videos you never finish.',
        bullets: ['System Design with real case studies', 'Pattern-based DSA', 'Agentic AI: tools, RAG and evals']
      },
      {
        visual: 'prep',
        kicker: 'Interview prep, free',
        title: 'Practise with questions answered in depth',
        text: 'Real interview questions with a 30-second answer, a deep dive, likely follow-ups and the mistakes to avoid. Free and open to everyone.',
        bullets: ['System design, DSA, Java, JavaScript and more', 'Agentic AI and behavioural rounds', 'No sign-up needed']
      }
    ],
    offer: {
      type: 'expert',
      title: 'Your next role starts with one conversation',
      text: 'Book a 30-minute call with a senior engineer at a time that suits you, and leave with a plan you can start the same day.',
      sticky: '1:1 call with a senior engineer'
    },
    live: {
      title: 'Upskill live, around your job',
      text: 'Evening and weekend batches on what product company and senior rounds actually test.',
      courses: ['System Design', 'Data Structures & Algorithms', 'Agentic AI']
    },
    prep: {
      title: 'Start preparing tonight, for free',
      text: 'In-depth interview questions for every round you will face.',
      tracks: [
        { slug: 'system-design', name: 'System Design', text: 'URL shortener, chat, feeds, payments and the building blocks behind them.' },
        { slug: 'dsa', name: 'Data Structures & Algorithms', text: 'Pattern-based problems with approach, complexity and follow-ups.' },
        { slug: 'agentic-ai', name: 'Agentic AI', text: 'RAG, tool use, agents, evaluation and security.' },
        { slug: 'java', name: 'Java & Spring', text: 'Collections internals, concurrency, JVM, Spring Boot and JPA.' },
        { slug: 'javascript', name: 'JavaScript', text: 'Closures, the event loop, promises and output puzzles.' },
        { slug: 'behavioural', name: 'Behavioural', text: 'STAR stories for ownership, conflict and leadership rounds.' }
      ]
    },
    compare: {
      title: 'Stop preparing in the dark',
      them: 'Preparing on your own',
      us: 'Preparing with Codeazy',
      rows: [
        ['Your plan', 'Guesswork from forums', 'Built 1:1 by a senior engineer'],
        ['DSA', 'Random problems', 'Pattern-based, taught live'],
        ['System design', 'Scattered videos and blogs', 'Live classes with real case studies'],
        ['AI skills', 'Self-taught, if at all', 'Agentic AI with tools, RAG and evals'],
        ['Interview practice', 'Hope for the best', 'In-depth questions with follow-ups']
      ]
    },
    journey: {
      title: 'Your plan to a better offer',
      steps: [
        { when: 'Week 1', title: 'Talk to an expert', text: 'Map your level to target companies, roles and gaps.' },
        { when: 'Months 1–3', title: 'Level up', text: 'DSA, system design and AI in live classes.' },
        { when: 'Alongside', title: 'Practise', text: 'In-depth interview questions for your target rounds.' },
        { when: 'Interview season', title: 'Make the switch', text: 'Apply with a stronger CV and interview with confidence.' }
      ]
    },
    faqs: [
      { q: 'Can I manage this with a full-time job?', a: 'Yes. Live classes run on weekday evenings and weekends, and the expert call is booked at a time that suits you.' },
      { q: 'I have 3+ years of experience. Is this too basic?', a: 'No. System Design and Agentic AI are aimed at experienced engineers. The expert call places you at the right level so you don’t repeat what you already know.' },
      { q: 'What happens on the expert call?', a: 'A senior engineer reviews your skills and CV against the roles you want, then gives you a step-by-step plan. You leave with a clear next step, whether or not you join a class.' },
      { q: 'Is the interview prep really free?', a: 'Yes. The interview questions and lessons are free to read, with no sign-up needed.' }
    ],
    finalCta: {
      title: 'The offer you want is a plan away.',
      text: 'Talk to a senior engineer and map your current level to the role, company and salary you are aiming for.',
      ctas: [
        { label: 'Talk to an expert', to: '#offer', primary: true },
        { label: 'Start interview prep', to: '/interview-prep' }
      ]
    }
  }
];

export const getAudience = (slug) => audiences.find((a) => a.slug === slug);
